import React, { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Check, Wine, Calculator } from 'lucide-react';

const BarServiceSection = ({ 
  formData,
  setFormData,
  selectedMenuIds,
  toggleMenuSelection,
  getMenusByCategory,
  sortMenuItems,
  barItems,
  selectedBarItems,
  toggleBarItem,
  isExpanded, 
  onToggle 
}) => {
  const [showBarItemsSection, setShowBarItemsSection] = useState(false);

  const barTypes = [
    { value: 'host_bar', label: 'Host Bar', desc: 'Client pays for all drinks', pricePerDrink: 6.00 },
    { value: 'cash_bar', label: 'Cash Bar', desc: 'Guests pay for their drinks', pricePerDrink: null },
    { value: 'toonie_bar', label: 'Toonie Bar', desc: '$2 per drink for guests', pricePerDrink: 4.00 },
    { value: 'ticket_bar', label: 'Ticket Bar', desc: 'Drink tickets provided', pricePerDrink: 6.00 }
  ];

  const barPackages = getMenusByCategory()['bar_package'] || [];
  
  // Calculate drink estimate
  const selectedBarType = barTypes.find(bt => bt.value === formData.barType);
  const showDrinkEstimate = selectedBarType && selectedBarType.pricePerDrink && formData.guestCount > 0;

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
      >
        <span className="flex items-center gap-2">
          🍸 Bar Service
          {formData.barType && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({formData.barType.replace(/_/g, ' ')})
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>
      
      {isExpanded && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          {/* Bar Type Selection */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Bar Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {barTypes.map((option) => (
                <div
                  key={option.value}
                  onClick={() => setFormData({ ...formData, barType: formData.barType === option.value ? '' : option.value })}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.barType === option.value
                      ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white'
                      : 'border-gray-200 bg-white hover:border-[#1e3a5f]'
                  }`}
                >
                  <p className="font-semibold text-sm">{option.label}</p>
                  <p className={`text-xs ${formData.barType === option.value ? 'text-gray-200' : 'text-gray-500'}`}>
                    {option.desc}
                  </p>
                  {option.pricePerDrink && (
                    <p className={`text-xs mt-1 ${formData.barType === option.value ? 'text-amber-300' : 'text-amber-600'}`}>
                      ${option.pricePerDrink.toFixed(2)}/drink
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Drink Estimation Calculator - only for host/toonie/ticket bars */}
          {showDrinkEstimate && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="drink-estimate-section">
              <div className="flex items-center gap-2 mb-3">
                <Calculator size={18} className="text-purple-600" />
                <Label className="text-sm font-medium text-purple-800">Estimated Drink Cost</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Drinks per Guest</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="10"
                    value={formData.drinksPerPerson || 3}
                    onChange={(e) => setFormData({ ...formData, drinksPerPerson: parseInt(e.target.value) || 3 })}
                    className="h-9"
                    data-testid="drinks-per-person-input"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <div className="bg-white rounded-lg p-2 border">
                    <p className="text-xs text-gray-500">Estimated Total</p>
                    <p className="text-lg font-bold text-purple-700" data-testid="drink-estimate-total">
                      ${(selectedBarType.pricePerDrink * (formData.drinksPerPerson || 3) * formData.guestCount).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                <Wine size={12} className="inline mr-1" />
                {formData.guestCount} guests × {formData.drinksPerPerson || 3} drinks × ${selectedBarType.pricePerDrink.toFixed(2)}/drink
              </p>
            </div>
          )}
          
          {/* Bar Packages from Menu */}
          {barPackages.length > 0 && (
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Select Bar Package</Label>
              <div className="space-y-2">
                {barPackages.map((menu) => {
                  const isSelected = selectedMenuIds.includes(menu.id);
                  return (
                    <div
                      key={menu.id}
                      onClick={() => toggleMenuSelection(menu.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white'
                          : 'border-gray-200 bg-white hover:border-[#1e3a5f]'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <p className="font-semibold">{menu.name}</p>
                          {menu.description && (
                            <p className={`text-xs ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                              {menu.description}
                            </p>
                          )}
                          {menu.service_hours && (
                            <p className={`text-xs mt-1 ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                              ⏱️ {menu.service_hours} hours of service
                              {menu.max_guests && ` • Up to ${menu.max_guests} guests`}
                              {menu.bartenders_included && ` • ${menu.bartenders_included} bartender${menu.bartenders_included > 1 ? 's' : ''}`}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0 min-w-[80px]">
                          <p className={`font-bold text-lg ${isSelected ? 'text-[#c9a227]' : 'text-amber-600'}`}>
                            ${menu.package_price || menu.price_per_person || 'N/A'}
                          </p>
                          <p className={`text-xs ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                            {menu.package_price ? 'flat fee' : menu.price_per_person ? '/person' : ''}
                          </p>
                        </div>
                      </div>
                      {isSelected && menu.items && menu.items.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/20">
                          <p className="text-xs mb-1">Includes:</p>
                          <div className="flex flex-wrap gap-1">
                            {sortMenuItems(menu.items).map((item, idx) => (
                              <span key={idx} className="text-xs bg-white/20 px-2 py-0.5 rounded">
                                {item.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selectable Bar Items */}
          {barItems.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowBarItemsSection(!showBarItemsSection)}
                className="flex items-center justify-between w-full text-left font-medium text-gray-700 mb-2"
              >
                <span>🍸 Select Bar Package ({selectedBarItems.length} selected)</span>
                <span className="text-sm text-gray-500">{showBarItemsSection ? '−' : '+'}</span>
              </button>
              {showBarItemsSection && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {barItems.map((item) => {
                    const isSelected = selectedBarItems.find(i => i.id === item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleBarItem(item)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#c9a227] bg-[#c9a227] text-white'
                            : 'border-gray-200 bg-white hover:border-[#c9a227]'
                        }`}
                        data-testid={`bar-item-${item.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {isSelected && <Check size={14} />}
                            <span className="font-medium">{item.name}</span>
                          </div>
                          {item.price_per_person && (
                            <span className="font-bold">${item.price_per_person}/person</span>
                          )}
                        </div>
                        {item.description && (
                          <p className={`text-xs mt-1 ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BarServiceSection;
